import { orgAuthFetch } from "@solomon-apps/shared";

import { CLIENT_OAUTH_CONFIG } from "@/constants/oauthConfig";
import type {
    ClientAccountDecisionTrailItem,
    ClientAccountDetailFilters,
    ClientAccountDetailResponse,
    ClientAccountFilters,
    ClientAccountProgramSurface,
    ClientAccountsResponse
} from "@/types/clientAccounts";

interface ErrorResponse {
    error?: string;
    message?: string;
}

type RawClientAccountProgramSurface = Omit<ClientAccountProgramSurface, "surfaceName" | "surfaceCategoryName"> & {
    surfaceName?: string;
    surfaceCategoryName?: string | null;
    poolName?: string;
    venueName?: string | null;
};

// The reporting endpoint may still emit the retired `venuePools` shape.
// Normalize it at the service boundary so the app consumes Program Surfaces.
type RawClientAccountDetailResponse = Omit<ClientAccountDetailResponse, "programSurfaces" | "decisionTrail"> & {
    programSurfaces?: RawClientAccountProgramSurface[];
    venuePools?: RawClientAccountProgramSurface[];
    // yaas-api dropped `decisionTrail` from the client account contract when
    // the policy-evaluation trail left the response, so it is absent on every
    // current payload. Optional on the wire, defaulted in the normalizer.
    decisionTrail?: ClientAccountDecisionTrailItem[];
};

const DATE_ONLY_FILTER = /^(\d{4})-(\d{2})-(\d{2})$/;

type DateFilterBoundary = "start" | "end";

async function readErrorMessage(response: Response): Promise<string> {
    const body = (await response.json().catch((): null => null)) as ErrorResponse | null;

    return body?.message ?? body?.error ?? `Accounts request failed: ${response.status}`;
}

async function readJsonResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        throw new Error(await readErrorMessage(response));
    }

    return response.json() as Promise<T>;
}

function serializeDateFilter(value: Date | string, boundary: DateFilterBoundary): string | null {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }

    const dateOnly = DATE_ONLY_FILTER.exec(value);
    if (!dateOnly) {
        return value;
    }

    const [, year, month, day] = dateOnly;
    const localBoundary =
        boundary === "start"
            ? new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0)
            : new Date(Number(year), Number(month) - 1, Number(day), 23, 59, 59, 999);

    return Number.isNaN(localBoundary.getTime()) ? null : localBoundary.toISOString();
}

function appendDateFilter(
    url: URL,
    key: "fromDate" | "toDate",
    value: Date | string | undefined,
    boundary: DateFilterBoundary
): void {
    if (!value) return;

    const serialized = serializeDateFilter(value, boundary);
    if (serialized) {
        url.searchParams.set(key, serialized);
    }
}

function appendFilters(url: URL, filters?: ClientAccountFilters | ClientAccountDetailFilters): void {
    if (!filters) return;

    appendDateFilter(url, "fromDate", filters.fromDate, "start");
    appendDateFilter(url, "toDate", filters.toDate, "end");
    if (filters.result) url.searchParams.set("result", filters.result);
    if (filters.search) url.searchParams.set("search", filters.search);
    if ("cycleUuid" in filters && filters.cycleUuid) url.searchParams.set("cycleUuid", filters.cycleUuid);
}

function normalizeProgramSurface(row: RawClientAccountProgramSurface): ClientAccountProgramSurface {
    return {
        ...row,
        surfaceName: row.surfaceName ?? row.poolName ?? "Program Surface",
        surfaceCategoryName: row.surfaceCategoryName ?? row.venueName ?? null
    };
}

function normalizeClientAccountDetail(raw: RawClientAccountDetailResponse): ClientAccountDetailResponse {
    const { venuePools, programSurfaces, decisionTrail, ...detail } = raw;
    const surfaceRows = programSurfaces ?? venuePools ?? [];

    return {
        ...detail,
        programSurfaces: surfaceRows.map(normalizeProgramSurface),
        decisionTrail: decisionTrail ?? []
    };
}

export async function fetchClientAccounts(filters?: ClientAccountFilters): Promise<ClientAccountsResponse> {
    const url = new URL(`${CLIENT_OAUTH_CONFIG.apiBase}/api/org/client/accounts`);
    appendFilters(url, filters);

    const response = await orgAuthFetch(url.toString(), undefined, CLIENT_OAUTH_CONFIG);

    return readJsonResponse<ClientAccountsResponse>(response);
}

export async function fetchClientAccountDetail(
    accountUuid: string,
    filters?: ClientAccountDetailFilters
): Promise<ClientAccountDetailResponse> {
    const url = new URL(`${CLIENT_OAUTH_CONFIG.apiBase}/api/org/client/accounts/${accountUuid}`);
    appendFilters(url, filters);

    const response = await orgAuthFetch(url.toString(), undefined, CLIENT_OAUTH_CONFIG);

    return normalizeClientAccountDetail(await readJsonResponse<RawClientAccountDetailResponse>(response));
}
